import { injectionFromAmount } from './roundFeedView.ts';

/**
 * 奖池「在涨」这件事该盯哪个字段。
 *
 * <p><b>不是 `pool.takeHome`。</b> 实测（2026-08-21，tier 4）：一期之内连续两笔注单
 * 进来，`roundFeed.tickets` 7→9、`injected` 14.00→18.00，而 `takeHome` 始终是
 * 5336.00 —— 它是**按期结算**的数字，只在跨期时跳一次。盯着它做注入动效，
 * 5 分钟档一期才可能闪一次，每小时档就是**一小时一次**，等于看不见。
 *
 * <p>会动的是本期投注额 `roundFeed.injected`。但它是**投注总额**，不是进池额：
 * 直接拿它当涨幅会虚报（进池只有 `jackpotContribPct`，其中还要留
 * `seedRetainPct` 给下一期）。所以走和购买页同一个公式 `injectionFromAmount`。
 */

/**
 * 此刻中奖能拿走的金额。
 *
 * <p><b>接口下发的 `pool.takeHome` 少报了本期。</b> 它是
 * `poolBefore × (1 − retain)`，而结算时用的是
 * {@code MilharJackpotMath.settle()}：
 *
 * <pre>
 *   contribution = roundStake × jackpotContribPct
 *   grossPool    = poolBefore + contribution      ← 本期自己的投注也在里面
 *   distributable= grossPool × (1 − seedRetainPct)
 * </pre>
 *
 * <p>展开就是 `takeHome + injectionFromAmount(本期投注额)` —— 所以把本期注入加上
 * 去不是把未结算的钱算进来，而是**补上一个少报**。同时它每有人下注就变，头条数字
 * 因此真的会涨，而且涨的是真数。
 *
 * <p>配置缺席时返回 `takeHome` 原值：宁可少报，也不要在奖池数字上编。
 */
export function livePoolTakeHome(
  takeHome: string | number | null | undefined,
  injected: string | number | null | undefined,
  contribPct: string | number | undefined,
  seedRetainPct: string | number | undefined,
): number {
  // null / undefined / '' 必须显式挡掉：`Number(null)` 是 **0**，不是 NaN，
  // 于是 takeHome 缺席时会返回一个只有本期注入那么大的"奖池" —— 一个小得离谱
  // 但看起来完全合理的数字。
  if (takeHome === null || takeHome === undefined || takeHome === '') return 0;
  const settled = Number(takeHome);
  if (!Number.isFinite(settled) || settled < 0) return 0;
  const thisRound = injectionFromAmount(injected ?? 0, contribPct, seedRetainPct);
  // 到分为止 —— 它会和到分的注入额并排显示
  return Math.round((settled + thisRound) * 100) / 100;
}

/**
 * 两次轮询之间，真正进入奖池的增量。
 *
 * @returns 涨幅（货币单位，已到分）；没有可播的涨幅时返回 null
 */
export function injectionDelta(
  previous: string | number | null | undefined,
  next: string | number | null | undefined,
  contribPct: string | number | undefined,
  seedRetainPct: string | number | undefined,
): number | null {
  const before = toAmount(previous);
  const after = toAmount(next);
  // 首帧没有对照物。进页面就飘一个「+R$ N」等于把历史累积伪装成刚刚发生的事。
  if (before === null || after === null) return null;
  // 跨期时 injected 归零，这是新一期开始，不是奖池缩水 —— 不播。
  if (after <= before) return null;
  const rise = injectionFromAmount(after - before, contribPct, seedRetainPct);
  // 比例缺席时 injectionFromAmount 返回 0：宁可不播，也不播一个编出来的数字。
  return rise > 0 ? rise : null;
}

function toAmount(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const amount = typeof value === 'string' ? Number(value) : value;
  return Number.isFinite(amount) && amount >= 0 ? amount : null;
}
